/*
  Invoicing tab pieces (UI overhaul wave 1, screen 07):
  - ContractMoneyBar — one stacked bar for the whole contract (base + approved VOs): received / pending /
                       not invoiced, so 498,855 = 437,850 base + 61,005 VOs is visible at a glance (idea 1)
  - InvoiceScopeSwitcher — scope as a segmented control with invoice counts instead of a dropdown (idea 3)
  Wave 2: PaymentMilestonesTable (idea 2), InvoicesTable + QuickBooksChip (ideas 4, 5, 9); the drawer is InvoicePanel.jsx.
  Wave 3: ReadyToBillCallout — work done in Execution but its payment not invoiced yet (idea 7); due dates and
  aging on every row ("Due 12 Sep · overdue 19 d", retention due after the 12-month DLP) and the overdue / DSO
  line under the money bar (idea 8).
  BUG-006 (owner decision 2026-10-04): deleting an invoice needs an Admin's approval. InvoicesTable puts the deletion
  items in each row's ⋯ (components/supply-finance/invoiceDeletion.js — Request deletion… for editors, Delete invoice…
  for admins, Approve / Reject / Cancel on a requested row) in place of the page's own "Delete invoice…", and shows
  "Deletion requested · <name>" under the row's status.
  QuickBooks Desktop (owner decision 9 Oct 2026, read only): InvoicesTable shows each row's match with the linked
  QuickBooks job (`qbMatchFor`); the QuickBooks panel itself is QbDesktopPanel.jsx.
*/
import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { CalendarClock, Check, CloudOff, FileCheck2, Image as ImageIcon, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StatusPill, ToneDot } from '@/components/ui/status-pill';
import { RowActions } from '@/components/ui/row-actions';
import { TableCheckbox } from '@/components/ui/table-checkbox';
import { cn } from '@/lib/utils';
import { formatDate, formatDateTime, formatNumber } from '@/lib/format';
import { Callout, MONO, Panel, ProgressBar, Segmented } from './ProjectUi';
import { invoiceDueState } from './invoicingModel';
import { DeletionRequestPill, useInvoiceDeletion } from '@/components/supply-finance/invoiceDeletion';
import { QbMatchChip } from '@/components/supply-finance/QbDesktopMatch';

const SEGMENT_FILL = {
  green: 'var(--status-green)',
  blue: 'var(--status-blue)',
  amber: 'var(--status-amber)',
  violet: 'var(--status-violet)',
  'violet-mid': 'color-mix(in srgb, var(--status-violet) 55%, white)',
  'violet-soft': 'color-mix(in srgb, var(--status-violet) 22%, white)',
};

const money = (value) => formatNumber(value, { decimals: 2 });

function LegendItem({ segment }) {
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-[12.5px] text-ink-3">
      <span className="h-2.5 w-2.5 shrink-0 rounded-[3px]" style={{ background: SEGMENT_FILL[segment.tone] }} aria-hidden="true" />
      {segment.label}
      <span className="font-semibold tabular-nums text-ink">{money(segment.amount)}</span>
    </span>
  );
}

export function ContractMoneyBar({ bar, approvedVariationCount = 0, collection }) {
  const baseSegments = bar.segments.filter((segment) => segment.group === 'base');
  const voSegments = bar.segments.filter((segment) => segment.group === 'variations');
  const hasVariations = bar.variationsValue > 0 || voSegments.some((segment) => segment.amount > 0);
  const visibleSegments = bar.segments.filter((segment) => segment.amount > 0 && (segment.group === 'base' || hasVariations));
  const description = visibleSegments.map((segment) => `${segment.label} ${money(segment.amount)}`).join(', ');

  return (
    <Panel className="px-4 py-4 sm:px-6" data-testid="contract-money-bar">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">Contract value</span>
          <span className="font-heading text-[24px] font-black tabular-nums text-ink" data-testid="contract-value">
            <span className="mr-1 text-[12px] font-bold text-ink-3">AED</span>
            {money(bar.total)}
          </span>
          <span className="text-[13px] text-ink-3">
            {money(bar.baseValue)} base
            {hasVariations ? ` + ${money(bar.variationsValue)} approved VO${approvedVariationCount === 1 ? '' : 's'}` : ''}
          </span>
        </div>
        <span className="text-[13px] text-ink-3">
          <span className="font-heading text-[16px] font-extrabold text-status-green-ink">{Math.round(bar.receivedPercent)}%</span> of contract received
        </span>
      </div>
      <div
        className="mt-3 flex h-3.5 w-full gap-[2px] overflow-hidden rounded-full bg-[color:var(--status-gray-bg)]"
        role="img"
        aria-label={`Contract ${money(bar.total)}: ${description}`}
      >
        {visibleSegments.map((segment) => (
          <span
            key={segment.key}
            className="h-full first:rounded-l-full last:rounded-r-full"
            style={{ width: `${segment.percent}%`, background: SEGMENT_FILL[segment.tone], minWidth: segment.percent > 0 ? 3 : 0 }}
            title={`${segment.label}: ${money(segment.amount)}`}
          />
        ))}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">Base</span>
        {baseSegments.map((segment) => <LegendItem key={segment.key} segment={segment} />)}
        {hasVariations ? (
          <>
            <span className="hidden h-4 w-px bg-line sm:inline-block" aria-hidden="true" />
            <span className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">Variations</span>
            {(voSegments.some((segment) => segment.amount > 0) ? voSegments.filter((segment) => segment.amount > 0) : voSegments.slice(-1))
              .map((segment) => <LegendItem key={segment.key} segment={segment} />)}
          </>
        ) : null}
      </div>
      {collection ? <CollectionLine collection={collection} /> : null}
    </Panel>
  );
}

/**
 * 07 #8: overdue receivables (submitted invoices past their due date), payments not invoiced on time (scheduled
 * rows past their "invoice by" day, retention past the DLP — not money the client owes yet) and DSO (average days
 * from submission to payment).
 */
function CollectionLine({ collection }) {
  const { overdueCount, overdueAmount, oldestOverdueDays, lateToInvoiceCount = 0, lateToInvoiceAmount = 0, dso, dsoBasis, raised } = collection;
  if (!overdueCount && !lateToInvoiceCount && dso === null && !raised) return null;
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-line-2 pt-2.5 text-[12.5px] text-ink-3" data-testid="invoice-collection">
      {overdueCount ? (
        <span className="inline-flex items-center gap-1.5 font-semibold text-status-red-ink" data-testid="invoice-collection-overdue">
          <ToneDot tone="red" />
          {overdueCount} overdue · {money(overdueAmount)}
          {oldestOverdueDays ? <span className="font-normal text-ink-3">· oldest {oldestOverdueDays} d</span> : null}
        </span>
      ) : <span>Nothing overdue</span>}
      {lateToInvoiceCount ? (
        <span
          className="inline-flex items-center gap-1.5 font-semibold text-status-amber-ink"
          title="Scheduled payments past the day they should have been invoiced, and retention past its defects period — not invoiced yet, so not counted as overdue"
          data-testid="invoice-collection-late"
        >
          <ToneDot tone="amber" />
          {lateToInvoiceCount} not invoiced on time · {money(lateToInvoiceAmount)}
        </span>
      ) : null}
      {dso !== null ? (
        <span title={`Average days from submission to payment, weighted by amount, over ${dsoBasis} paid invoice${dsoBasis === 1 ? '' : 's'} with both dates recorded`}>
          DSO <b className="font-semibold tabular-nums text-ink">{dso} d</b>
          <span className="text-ink-4"> · {dsoBasis} paid invoice{dsoBasis === 1 ? '' : 's'}</span>
        </span>
      ) : <span className="text-ink-4">DSO — no paid invoice with both dates yet</span>}
    </div>
  );
}

/**
 * 07 #7 "Ready to bill": a scheduled payment whose work is done in Execution — "Devices Installation is 24/24
 * Done, but Installation 193,782.82 (44.26%) has not been invoiced yet" + Raise invoice.
 */
export function ReadyToBillCallout({ items = [], onRaise, canRaise = true }) {
  if (!items.length) return null;
  const [first] = items;
  const evidence = first.evidence?.total
    ? `${first.evidence.title} is ${first.evidence.done}/${first.evidence.total} Done`
    : `${first.evidence?.title || 'Its work'} is Done`;
  return (
    <Callout
      tone="lime"
      icon={Zap}
      data-testid="invoices-ready-to-bill"
      actions={onRaise && canRaise ? (
        <Button type="button" size="sm" variant="outline" onClick={() => onRaise(first.invoice)} data-testid="invoices-ready-raise">
          Raise invoice
        </Button>
      ) : null}
    >
      <b className="font-semibold text-ink">Ready to bill:</b>
      {` ${evidence}, but ${first.types.join(', ')} `}
      <b className="font-semibold tabular-nums text-ink">{money(first.amount)}</b>
      {first.share !== null ? ` (${pctText(first.share)})` : ''}
      {' has not been invoiced yet.'}
      {items.length > 1 ? ` ${items.length - 1} more scheduled payment${items.length === 2 ? ' is' : 's are'} ready too.` : ''}
    </Callout>
  );
}

// Each part of the due line stays whole; the line may wrap between parts (a narrow Type column, 07 #4).
const Part = ({ children }) => <span className="whitespace-nowrap">{children}</span>;
const ESTIMATED_SUBMITTED = 'Submitted date not recorded for this older invoice — this is the day it was last changed';

/** 07 #8: the due / aging line under an invoice's type. */
function DueLine({ invoice, handoverDate, handedOver, now }) {
  const state = invoiceDueState(invoice, { handoverDate, handedOver, now });
  if (!state || state.state === 'paid') return null;
  const testId = `invoice-due-${invoice.id}`;
  if (state.state === 'open') {
    return (
      <span className="block text-[12px] text-ink-3" data-testid={testId} title={state.estimated ? ESTIMATED_SUBMITTED : undefined}>
        <Part>Submitted {state.estimated ? '≈ ' : ''}{formatDate(state.submittedAt, { year: 'auto', now })}</Part> · <Part>{state.age} d</Part> · <Part>no due date</Part>
      </span>
    );
  }
  if (state.state === 'handover_pending') {
    return (
      <span className="block text-[12px] text-ink-3" data-testid={testId} title={`The 12-month defects period starts at handover (planned ${formatDate(state.handoverDate)}, not handed over yet)`}>
        <Part>Due 12 months after handover</Part> · <Part>handover pending</Part>
      </span>
    );
  }
  if (state.source === 'dlp') {
    return state.state === 'overdue'
      ? <span className="block text-[12px] font-semibold text-status-amber-ink" data-testid={testId}><Part>DLP ended {formatDate(state.dueDate)}</Part> · <Part>{state.days} d ago</Part></span>
      : <span className="block text-[12px] text-ink-3" data-testid={testId}><Part>Due after 12-month DLP</Part> · <Part>{formatDate(state.dueDate)}</Part></span>;
  }
  // A scheduled row's date is when to INVOICE it: late to invoice (amber), not money the client owes yet
  const lead = state.scheduled ? 'Invoice by' : 'Due';
  if (state.state === 'overdue') {
    return (
      <span className="block text-[12px] text-ink-3" data-testid={testId}>
        <Part>{lead} {formatDate(state.dueDate)}</Part> · {state.scheduled
          ? <b className="whitespace-nowrap font-semibold text-status-amber-ink">{state.days} d late</b>
          : <b className="whitespace-nowrap font-semibold text-status-red-ink">overdue {state.days} d</b>}
      </span>
    );
  }
  return (
    <span className="block text-[12px] text-ink-3" data-testid={testId}>
      <Part>{lead} {formatDate(state.dueDate)}</Part>{state.days === 0 ? <> · <Part>today</Part></> : <> · <Part>in {-state.days} d</Part></>}
    </span>
  );
}

export function InvoiceScopeSwitcher({ value, onChange, variations = [], counts = {}, className }) {
  const options = [
    { key: 'base', label: 'Base scope', count: counts.base ?? 0, testId: 'invoice-scope-base' },
    ...[...variations].sort((a, b) => String(a.vo_number || '').localeCompare(String(b.vo_number || ''), undefined, { numeric: true })).map((variation) => ({
      key: variation.id,
      label: variation.vo_number || 'Variation',
      mono: true,
      count: counts[variation.id] ?? 0,
      dot: variation.status === 'Approved' ? undefined : (variation.status === 'Rejected' ? 'red' : 'amber'),
      title: `${variation.vo_number || 'Variation'} · ${variation.description || 'No description'} · ${variation.status || 'Proposed'}`,
      testId: `invoice-scope-${variation.id}`,
    })),
  ];
  if (variations.length) {
    options.push({
      key: 'total',
      label: 'All',
      count: counts.total ?? 0,
      title: 'Base scope + approved variations',
      testId: 'invoice-scope-total',
    });
  }
  return (
    <Segmented
      ariaLabel="Invoice scope"
      options={options}
      value={value}
      onChange={onChange}
      className={cn('max-w-full', className)}
    />
  );
}

/* ---------------------------------------------------------------- wave 2 (screen 07 ideas 2, 4, 5, 9) */

const MILESTONE_LABELS = {
  'Advanced payment': 'Advanced payment',
  'Against Delivery': 'Material delivery',
  'Cable pulling': 'Cable pulling',
  Installation: 'Installation',
  'T&C': 'T&C',
  'Handing Over': 'Handing over',
  Retention: 'Retention',
  Unallocated: 'Not allocated to a term',
};

const pct = (part, whole) => (whole > 0 ? (part / whole) * 100 : 0);
const pctText = (value) => `${formatNumber(value, { decimals: 2 })}%`;

/**
 * Payment milestones as one table (07 #2) instead of seven cards: share of the scope, amount, received,
 * pending, not invoiced and a received bar per term. Terms with no amount are muted.
 */
export function PaymentMilestonesTable({ summary, scopeValue, title, meta, totals, note }) {
  const rows = (summary?.byType || []).filter((row) => row.totalAmount > 0 || MILESTONE_LABELS[row.key]);
  const total = rows.reduce((acc, row) => ({
    amount: acc.amount + row.totalAmount,
    received: acc.received + row.received,
    pending: acc.pending + row.pending,
    notInvoiced: acc.notInvoiced + row.notInvoiced,
  }), { amount: 0, received: 0, pending: 0, notInvoiced: 0 });
  const cell = 'border-b border-line-2 px-3 py-2 text-[13px]';
  const head = 'h-9 border-b border-line bg-surface-2 px-3 text-[11.5px] font-bold text-ink-3';
  const dash = <span className="text-ink-4">—</span>;
  return (
    <Panel className="overflow-hidden" data-testid="payment-summary">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-line px-4 py-3 sm:px-5">
        <h3 className="text-[14px] font-bold text-ink">{title}</h3>
        {meta ? <div className="text-[12px] text-ink-3">{meta}</div> : null}
        {note ? <p className="basis-full text-[12px] text-ink-3">{note}</p> : null}
      </div>
      {totals}
      <div className="relative overflow-x-auto">
        <table className="w-full min-w-[720px] border-separate border-spacing-0">
          <thead>
            <tr>
              <th className={cn(head, 'text-left')}>Milestone</th>
              <th className={cn(head, 'text-right')}>Share</th>
              <th className={cn(head, 'text-right')}>Amount</th>
              <th className={cn(head, 'text-right')}>Received</th>
              <th className={cn(head, 'text-right')}>Pending</th>
              <th className={cn(head, 'text-right')}>Not invoiced</th>
              <th className={cn(head, 'w-[210px] text-left')}>Progress</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const muted = row.totalAmount <= 0;
              const progress = pct(row.received, row.totalAmount);
              const label = MILESTONE_LABELS[row.key] || row.label;
              return (
                <tr key={row.key} data-testid={`milestone-${row.key}`}>
                  <td className={cn(cell, muted ? 'text-ink-3' : 'font-semibold text-ink')}>{label}</td>
                  <td className={cn(cell, 'text-right tabular-nums', muted ? 'text-ink-3' : 'text-ink')}>{pctText(pct(row.totalAmount, scopeValue))}</td>
                  <td className={cn(cell, 'text-right tabular-nums text-ink')}>{muted ? dash : money(row.totalAmount)}</td>
                  <td className={cn(cell, 'text-right tabular-nums', row.received > 0 ? 'font-semibold text-status-green-ink' : 'text-ink-2')}>{muted ? dash : money(row.received)}</td>
                  <td className={cn(cell, 'text-right tabular-nums', row.pending > 0 ? 'font-semibold text-status-blue-ink' : 'text-ink-2')}>{muted ? dash : money(row.pending)}</td>
                  <td className={cn(cell, 'text-right tabular-nums', row.notInvoiced > 0 ? 'font-semibold text-status-amber-ink' : 'text-ink-2')}>{muted ? dash : money(row.notInvoiced)}</td>
                  <td className={cell}>
                    {muted ? dash : (
                      <span className="flex items-center gap-2">
                        <ProgressBar value={progress} tone={progress >= 100 ? 'green' : progress > 0 ? 'amber' : 'gray'} className="w-[110px]" label={`${label} received`} />
                        <span className="text-[12px] font-semibold tabular-nums text-ink-2">{Math.round(progress)}%</span>
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-surface-2" data-testid="milestones-total">
              <td className="px-3 py-2.5 text-[13px] font-bold text-ink">Total</td>
              <td className="px-3 py-2.5 text-right text-[13px] font-bold tabular-nums text-ink">{pctText(pct(total.amount, scopeValue))}</td>
              <td className="px-3 py-2.5 text-right text-[13px] font-bold tabular-nums text-ink">{money(total.amount)}</td>
              <td className="px-3 py-2.5 text-right text-[13px] font-bold tabular-nums text-ink">{money(total.received)}</td>
              <td className="px-3 py-2.5 text-right text-[13px] font-bold tabular-nums text-ink">{money(total.pending)}</td>
              <td className="px-3 py-2.5 text-right text-[13px] font-bold tabular-nums text-ink">{money(total.notInvoiced)}</td>
              <td className="px-3 py-2.5">
                <span className="flex items-center gap-2">
                  <ProgressBar value={pct(total.received, total.amount)} tone="green" className="w-[110px]" label="Received of the scope" />
                  <span className="whitespace-nowrap text-[12px] tabular-nums text-ink-2">{Math.round(pct(total.received, total.amount))}%</span>
                </span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </Panel>
  );
}

/** QuickBooks state of one invoice (07 #9): "QB synced" / "Not synced"; scheduled rows have none. */
export function QuickBooksChip({ invoice }) {
  if ((invoice.status || 'Not Started') === 'Not Started') return <span className="text-ink-4" aria-label="Not invoiced yet">—</span>;
  if (invoice.quickbooks?.invoice_id) {
    const number = invoice.quickbooks.doc_number || invoice.quickbooks.invoice_id;
    const when = invoice.quickbooks.synced_at ? ` · ${formatDateTime(invoice.quickbooks.synced_at)}` : '';
    return <StatusPill tone="green" icon={Check} size="sm" label="QB synced" title={`QuickBooks #${number}${when}`} />;
  }
  return <StatusPill tone="outline" size="sm" label="Not synced" />;
}

/**
 * Why "Sync to QuickBooks" is unavailable, as visible text (a disabled button's title reaches neither
 * keyboard, touch nor screen-reader users). The sync buttons point at it with aria-describedby={id}.
 * status: GET /quickbooks/status ({ configured, connected }) or null when it could not be read.
 */
export function QuickBooksSyncNote({ id, status, canSetUp = false, className }) {
  let reason = 'QuickBooks status could not be checked';
  if (status && !status.configured) reason = 'QuickBooks sync is switched off on this server (not configured)';
  else if (status && !status.connected) reason = 'QuickBooks is not connected';
  return (
    <p id={id} className={cn('flex items-start gap-1.5 text-[12.5px] leading-[1.45] text-ink-3', className)} data-testid="invoices-qb-note">
      <CloudOff className="mt-[2px] size-3.5 shrink-0" aria-hidden="true" />
      <span>
        {reason}, so invoices cannot be synced yet.{' '}
        {canSetUp
          ? <Link to="/admin?tab=integrations" className="whitespace-nowrap font-semibold text-brand-ink underline-offset-2 hover:underline">Set it up in Admin › Integrations</Link>
          : 'An admin can set it up in Admin › Integrations.'}
      </span>
    </p>
  );
}

/**
 * Invoices for reading (07 #4, #5): "Issued" (submitted / paid) and "Scheduled · not invoiced yet"; each row is
 * number, type, status, amount, % of scope, note, QuickBooks and one ⋯ menu; scheduled rows have "Raise invoice".
 * Clicking a row opens the drawer. Photos show as an icon only when there are any.
 * BUG-006: with a `rowMenu` and `canEdit`, the menu gets the deletion items for this user (an item with the test id
 * `invoice-delete-<id>` from `rowMenu` is replaced by them); `onInvoicesChanged` reloads the invoices afterwards so
 * the page's totals follow (without it only this table's row changes at once). A deleted invoice is unselected
 * through `onToggle(id, false)`.
 * QuickBooks Desktop (read only, QbDesktopPanel.jsx): `qbMatchFor(invoice)` → the invoice's match with the linked
 * QuickBooks job, shown as a chip under the QuickBooks Online state ("Paid in QuickBooks", "Not in QuickBooks", …).
 */
export function InvoicesTable({
  groups: groupsProp,
  scopeValue,
  selectedIds = [],
  onToggle,
  onToggleAll,
  onOpen,
  onRaise,
  rowMenu,
  canEdit,
  showScopeCode,
  footer,
  handoverDate,
  handedOver,
  now,
  onInvoicesChanged,
  qbMatchFor,
}) {
  const deletion = useInvoiceDeletion({
    onChanged: onInvoicesChanged,
    // the bulk bar (Export, Sync to QuickBooks) must not keep acting on an invoice that is gone
    onDeleted: (invoice) => onToggle?.(invoice.id, false),
  });
  const groups = groupsProp.map((group) => ({ ...group, rows: group.rows.map(deletion.apply).filter(Boolean) }));
  const menuFor = (invoice) => [
    ...rowMenu(invoice).filter((item) => !(item && item.testId === `invoice-delete-${invoice.id}`)),
    ...deletion.menuItems(invoice, { canEdit }),
  ];
  const head = 'h-10 whitespace-nowrap border-b border-line bg-surface-2 px-3 text-left text-[11.5px] font-bold text-ink-3';
  const cell = 'border-b border-line-2 px-3 py-2 align-middle text-[13px]';
  const allRows = groups.flatMap((group) => group.rows);
  const allSelected = allRows.length > 0 && allRows.every((invoice) => selectedIds.includes(invoice.id));
  const someSelected = allRows.some((invoice) => selectedIds.includes(invoice.id));
  return (
    <div data-testid="invoices-table">
    {/* relative: containing block of the sr-only header inside the scroller (it must not widen the page) */}
    <div className="relative overflow-x-auto">
      <table className="w-full min-w-[860px] border-separate border-spacing-0">
        <thead>
          <tr>
            <th className={cn(head, 'w-10 pr-0')}>
              <TableCheckbox checked={allSelected} indeterminate={!allSelected && someSelected} onCheckedChange={onToggleAll} aria-label="Select all invoices" />
            </th>
            <th className={head}>Invoice</th>
            <th className={head}>Type</th>
            <th className={head}>Status</th>
            <th className={cn(head, 'text-right')}>Amount</th>
            <th className={cn(head, 'text-right')}>% of scope</th>
            {/* Note takes whatever width is left (w-full + max-w-0 lets it shrink and truncate) */}
            <th className={cn(head, 'w-full max-w-0')}>Note</th>
            <th className={head}>QuickBooks</th>
            <th className={cn(head, 'w-px')}><span className="sr-only">Actions</span></th>
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => (
            <Fragment key={group.key}>
              <tr className="bg-surface-2" data-testid={`invoice-group-${group.key}`}>
                <td colSpan={9} className="border-b border-line-2 px-3 py-2">
                  <span className="inline-flex flex-wrap items-center gap-x-2 text-[12.5px] text-ink-3">
                    {group.key === 'scheduled' ? <CalendarClock className="h-3.5 w-3.5" aria-hidden="true" /> : <FileCheck2 className="h-3.5 w-3.5" aria-hidden="true" />}
                    <b className="font-bold text-ink">{group.label}</b>
                    <span>· {group.meta}</span>
                    <span>· <b className="font-semibold tabular-nums text-ink">{money(group.total)}</b></span>
                  </span>
                </td>
              </tr>
              {group.rows.map((invoice) => {
                const selected = selectedIds.includes(invoice.id);
                const photos = [invoice.invoice_reference_photo_url, invoice.invoice_photo_url].filter(Boolean).length;
                const scheduled = (invoice.status || 'Not Started') === 'Not Started';
                const types = String(invoice.invoice_type || '').split(',').map((type) => type.trim()).filter(Boolean);
                const split = types.length > 1
                  ? Object.entries(invoice.invoice_type_percentages || {}).map(([type, value]) => `${type} ${value}%`).join(' · ')
                  : undefined;
                const qbMatch = qbMatchFor ? qbMatchFor(invoice) : null;
                return (
                  <tr
                    key={invoice.id}
                    className={cn('cursor-pointer transition-colors', selected ? 'bg-brand-soft' : 'hover:bg-surface-hover')}
                    onClick={(event) => {
                      if (event.target.closest('button, a, input, [role="checkbox"], [data-row-click="ignore"]')) return;
                      onOpen?.(invoice);
                    }}
                    data-testid={`invoice-row-${invoice.id}`}
                  >
                    <td className={cn(cell, 'w-10 pr-0')} data-row-click="ignore">
                      <TableCheckbox
                        checked={selected}
                        onCheckedChange={(checked) => onToggle?.(invoice.id, checked)}
                        aria-label={`Select invoice ${invoice.reference || types.join(', ')}`}
                      />
                    </td>
                    <td className={cn(cell, 'whitespace-nowrap')}>
                      <span className="inline-flex items-center gap-1.5">
                        {invoice.reference
                          ? <span className={cn(MONO, 'text-[12.5px] font-semibold text-ink')}>{invoice.reference}</span>
                          : <span className="text-ink-4" aria-label="No invoice number">—</span>}
                        {photos ? (
                          <span title={`${photos} photo${photos === 1 ? '' : 's'} attached`}>
                            <ImageIcon className="h-3.5 w-3.5 text-ink-3" aria-label={`${photos} photo${photos === 1 ? '' : 's'}`} />
                          </span>
                        ) : null}
                      </span>
                    </td>
                    <td className={cn(cell, 'min-w-[150px]')}>
                      <span className="flex min-w-0 flex-wrap items-center gap-1.5">
                        <span className="text-ink" title={split}>{types.join(', ') || '—'}</span>
                        {showScopeCode && invoice.scopeCode ? <StatusPill tone="violet" size="sm" label={invoice.scopeCode} /> : null}
                      </span>
                      <DueLine invoice={invoice} handoverDate={handoverDate} handedOver={handedOver} now={now} />
                    </td>
                    <td className={cell}>
                      <StatusPill status={invoice.status || 'Not Started'} domain="invoice" size="sm" />
                      {invoice.deletion_request ? (
                        <span className="mt-1 flex"><DeletionRequestPill invoice={invoice} className="whitespace-nowrap" /></span>
                      ) : null}
                    </td>
                    <td className={cn(cell, 'text-right font-semibold tabular-nums text-ink')}>{money(invoice.amount)}</td>
                    <td className={cn(cell, 'text-right tabular-nums text-ink-2')}>{pctText(pct(Number(invoice.amount || 0), scopeValue))}</td>
                    <td className={cn(cell, 'w-full max-w-0 truncate text-ink-3')} title={invoice.note || undefined}>{invoice.note || <span className="text-ink-4">—</span>}</td>
                    <td className={cell}>
                      <QuickBooksChip invoice={invoice} />
                      {qbMatch ? <span className="mt-1 flex"><QbMatchChip match={qbMatch} testId={`invoice-qbd-${invoice.id}`} /></span> : null}
                    </td>
                    <td className={cn(cell, 'w-px whitespace-nowrap pr-2 text-right')} data-row-click="ignore">
                      <span className="inline-flex items-center gap-1">
                        {scheduled && canEdit ? (
                          <Button type="button" size="sm" variant="outline" onClick={() => onRaise?.(invoice)} data-testid={`invoice-raise-${invoice.id}`}>
                            Raise invoice
                          </Button>
                        ) : null}
                        {rowMenu ? (
                          <RowActions
                            size="sm"
                            label={`Actions for invoice ${invoice.reference || types.join(', ')}`}
                            items={menuFor(invoice)}
                            contentClassName="min-w-[260px]"
                            data-testid={`invoice-actions-${invoice.id}`}
                          />
                        ) : null}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {!group.rows.length ? (
                <tr><td colSpan={9} className="border-b border-line-2 px-4 py-3 text-[12.5px] text-ink-3">{group.empty}</td></tr>
              ) : null}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
    {footer}
    </div>
  );
}
